import React from "react";
import { Box, Grid } from "@mui/material";
import {
  SolarPowerOutlined, WbSunnyOutlined, OfflineBoltOutlined,
  Co2Outlined, FactoryOutlined, SpeedOutlined,
} from "@mui/icons-material";
import {
  AreaChart, Area, BarChart, Bar, RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";
import DemoShell from "./DemoShell";
import { MetricCard, SectionTitle, ChartCard, DarkTooltip } from "./demoComponents";
import { demo } from "./demoStyles";

// ── Mock data ────────────────────────────────────────────────────────────────
// Bell-curve-ish solar generation across the day
const hourlyGen = Array.from({ length: 24 }, (_, h) => {
  const raw = h >= 6 && h <= 18
    ? Math.round(Math.sin(((h - 6) / 12) * Math.PI) * 3800 + (Math.random() - 0.5) * 200)
    : 0;
  return { hour: `${h}:00`, MW: Math.max(0, raw) };
});

const monthlyGen = [
  { month: "Nov", actual: 28400, target: 30000 },
  { month: "Dec", actual: 24200, target: 26000 },
  { month: "Jan", actual: 26800, target: 27000 },
  { month: "Feb", actual: 31200, target: 30000 },
  { month: "Mar", actual: 37600, target: 35000 },
  { month: "Apr", actual: 42100, target: 40000 },
  { month: "May", actual: 44800, target: 42000 },
];

const plantPerf = [
  { name: "Rajasthan",    pr: 84, fill: "#3b82f6" },
  { name: "Gujarat",      pr: 81, fill: "#22d3ee" },
  { name: "Maharashtra",  pr: 79, fill: "#34d399" },
  { name: "Punjab",       pr: 76, fill: "#fbbf24" },
  { name: "UP",           pr: 72, fill: "#a78bfa" },
];

const GRADIENT = "linear-gradient(135deg, #fbbf24, #f59e0b)";

export default function SolarAnalyticsPage() {
  return (
    <DemoShell title="Solar Analytics" icon={SolarPowerOutlined} gradient={GRADIENT}>
      {/* ── KPI row ── */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[
          { icon: SolarPowerOutlined,  label: "Total Capacity",      value: "42",    unit: "MW",    sub: "Across 42 plants",          gradient: GRADIENT },
          { icon: OfflineBoltOutlined, label: "Today's Generation",  value: "186",   unit: "MWh",   trend: "+8% vs yesterday", trendUp: true,  gradient: GRADIENT },
          { icon: SpeedOutlined,       label: "Performance Ratio",   value: "81",    unit: "%",     trend: "-2% vs target",   trendUp: false, gradient: GRADIENT },
          { icon: WbSunnyOutlined,     label: "Irradiance",          value: "4.8",   unit: "kWh/m²", sub: "Daily average",     gradient: GRADIENT },
          { icon: FactoryOutlined,     label: "Plants Online",       value: "38",    unit: "/ 42",  sub: "4 under maintenance",       gradient: "linear-gradient(135deg, #34d399, #22d3ee)" },
          { icon: Co2Outlined,         label: "CO₂ Offset",          value: "142",   unit: "T",     trend: "+18% this month", trendUp: true,  gradient: "linear-gradient(135deg, #34d399, #22d3ee)" },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <MetricCard {...m} />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts row 1 ── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {/* Hourly generation */}
        <Grid item xs={12} lg={8}>
          <ChartCard title="Today's Generation Curve" subtitle="Instantaneous output in MW · Hourly">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={hourlyGen} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="solarGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#fbbf24" stopOpacity={0.40} />
                    <stop offset="95%" stopColor="#fbbf24" stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="hour" tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} interval={3} />
                <YAxis tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<DarkTooltip unit=" MW" />} />
                <Area type="monotone" dataKey="MW" stroke="#fbbf24" strokeWidth={2.5}
                  fill="url(#solarGrad)" dot={false} activeDot={{ r: 5, fill: "#fbbf24" }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Performance ratio by state */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard title="Performance Ratio by State" subtitle="PR % vs benchmark (85%)">
            <ResponsiveContainer width="100%" height={240}>
              <RadialBarChart cx="50%" cy="50%" innerRadius={20} outerRadius={95}
                data={plantPerf} startAngle={90} endAngle={-270}>
                <RadialBar minAngle={15} dataKey="pr" cornerRadius={4} background={{ fill: "rgba(255,255,255,0.04)" }}>
                  {plantPerf.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </RadialBar>
                <Legend
                  iconSize={8}
                  formatter={(v, e) => (
                    <span style={{ color: demo.textDim, fontSize: 11.5 }}>
                      {e.payload.name} <span style={{ color: e.payload.fill, fontWeight: 700 }}>{e.payload.pr}%</span>
                    </span>
                  )}
                />
                <Tooltip
                  content={({ active, payload }) =>
                    active && payload?.length
                      ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "8px", px: 1.6, py: 1 }}>
                          <span style={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>{payload[0].payload.name}: {payload[0].value}% PR</span>
                        </Box>
                      : null}
                />
              </RadialBarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* ── Monthly bar chart ── */}
      <ChartCard title="Monthly Generation vs Target (MWh)" subtitle="Last 7 months · All plants combined">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={monthlyGen} margin={{ top: 4, right: 4, bottom: 0, left: -10 }} barGap={6}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: demo.textFaint, fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
            <Tooltip content={<DarkTooltip unit=" MWh" />} />
            <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12 }}>{v === "actual" ? "Actual" : "Target"}</span>} />
            <Bar dataKey="actual" fill="#fbbf24" radius={[5,5,0,0]} maxBarSize={36} />
            <Bar dataKey="target" fill="rgba(251,191,36,0.22)" radius={[5,5,0,0]} maxBarSize={36} stroke="#fbbf24" strokeWidth={1} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </DemoShell>
  );
}
